import { CustomModule, NGSModule, AsyncInit, SyncInit } from "~/core/module";
import { ModuleKey } from "~/modules/registry";

export interface ModuleManager {
  register(module: CustomModule): void;
  initialize(): Promise<void>;
  destroyAllModules(): void;
  get(name: string): CustomModule | null;
}

export class NGSModuleManager implements ModuleManager {
  private modules = new Map<ModuleKey, NGSModule>();
  private instances = new Set<ModuleKey>();
  private currentPath: string;

  constructor() {
    console.log("NGS Module Manager Initialized ✈️");
    this.currentPath = window.location.pathname;
  }

  /**
   * Checks if module should be initialized for current route
   */
  private shouldInitializeForRoute(module: NGSModule): boolean {
    const meta = module["_metadata"];
    // If no routes specified, initialize everywhere
    if (!meta.routes || meta.routes.length === 0) return true;

    // Check if current path starts with any of the specified routes
    return meta.routes.some((route) => this.currentPath.startsWith(route));
  }

  /**
   * Registers a module
   * @param NGSModule - Instance of the module
   */
  public register(module: NGSModule): void {
    if (this.modules.has(module.key)) {
      throw new Error(`Module ${module.name} already registered`);
    }

    this.modules.set(module.key, module);
  }

  /**
   * Initializes all modules
   */
  public async initialize() {
    for (const [name] of this.modules) {
      await this.initializeModule(name);
    }
  }

  /**
   * Removes all module from the manager
   */
  public destroyAllModules() {
    this.instances.forEach((name) => {
      const module = this.modules.get(name);
      module?.destroy();
    });
    this.instances.clear();
  }

  /**
   * Returns an instance of a module
   * @param name
   * @returns
   */
  public get(name: ModuleKey): NGSModule | null {
    return this.modules.get(name) ?? null;
  }

  /**
   * Initializes a module
   * @param name - The name of the module
   */
  private async initializeModule(name: ModuleKey) {
    if (this.instances.has(name)) return;

    const module = this.modules.get(name);

    if (!module) {
      throw new Error(`Module ${name} not found`);
    }

    // Skip initialization if route doesn't match
    if (!this.shouldInitializeForRoute(module)) {
      return;
    }

    // Initialize dependencies first
    for (const dep of module.dependencies) {
      if (this.instances.has(dep)) continue;

      if (!this.modules.has(dep)) {
        throw new Error(`Module ${dep} not found, required by ${name}`);
      }

      await this.initializeModule(dep);
    }

    try {
      // Handle both sync and async initialization
      if (this.isAsyncInit(module)) {
        await module.init();
      } else if (this.isSyncInit(module)) {
        module.init();
      } else {
        throw new Error(
          `Module ${name} must implement either AsyncInit or SyncInit`
        );
      }

      if (module.hasErrors) {
        throw new Error(
          `[${module.name}]: Module reported errors during initialization`
        );
      }

      this.instances.add(name);
      console.log(`[${module.name}]: Module initialized.`);
    } catch (error) {
      if (module.hasErrors) {
        console.error(
          `[${module.name}]: Module reported the following errors:`
        );
        module.errors.forEach((err) =>
          console.error(`[${module.name}]: ${err}`)
        );
      } else {
        console.error(`[${module.name}]: ${error}`);
      }
      throw error; // Re-throw to prevent partial initialization
    }
  }

  /**
   * Type guard for AsyncInit modules
   */
  private isAsyncInit(module: any): module is AsyncInit {
    return (
      "init" in module &&
      module.init instanceof Function &&
      module.init.constructor.name === "AsyncFunction"
    );
  }

  /**
   * Type guard for SyncInit modules
   */
  private isSyncInit(module: any): module is SyncInit {
    return (
      "init" in module &&
      module.init instanceof Function &&
      module.init.constructor.name !== "AsyncFunction"
    );
  }
}
